import { useState } from 'react';
import { useNavigate, useSearchParams, Navigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { homePathForRole } from '@/lib/rbac';
import { APP_VERSION } from '@/lib/version';

const schema = z.object({
  username: z.string().min(1, 'errors.validation.usernameRequired'),
  password: z.string().min(1, 'errors.validation.passwordRequired'),
});

type FormValues = z.infer<typeof schema>;

export default function LoginPage() {
  const { isAuthenticated, user, signIn } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next');
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    // Invite flow (#81): set-password success redirects here with ?username= to prefill.
    defaultValues: { username: searchParams.get('username') ?? '', password: '' },
  });

  if (isAuthenticated && user) {
    return <Navigate to={next ?? homePathForRole(user.role)} replace />;
  }

  const onSubmit = async (data: FormValues) => {
    clearErrors('root');
    try {
      const me = await signIn(data.username, data.password);
      // Temp-password fallback (#78): force the change before the app.
      if (me?.mustChangePassword) {
        navigate('/change-password', { replace: true });
        return;
      }
      navigate(next ?? homePathForRole(me.role), { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setError('root', { message: t('login.invalidCredentials') });
      } else if (error instanceof ApiError && error.status === 429) {
        setError('root', { message: t('login.rateLimited') });
      } else {
        toast.error(t('login.unreachable'));
      }
    }
  };

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-background p-4">
      {/* Quiet tonal field: a barely-there radial lift of the graphite */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 90% at 50% 0%, hsl(var(--accent) / 0.55) 0%, hsl(var(--background)) 62%)',
        }}
      />
      {/* Oversized faint wordmark */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-[0.16em] left-1/2 -translate-x-1/2 select-none whitespace-nowrap font-serif italic leading-none text-foreground/[0.025]"
        style={{ fontSize: 'clamp(140px, 26vw, 420px)' }}
      >
        License
      </span>

      <div className="relative w-full max-w-[400px] motion-safe:duration-500 motion-safe:animate-in motion-safe:fade-in">
        <div className="rounded-[10px] border bg-card px-6 py-8 shadow-[0_1px_0_0_hsl(var(--foreground)/0.03),0_16px_48px_-16px_hsl(33_18%_4%/0.55)] sm:px-9 sm:py-10">
          {/* Wordmark + env */}
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2.5">
              <span className="h-2.5 w-2.5 rounded-[3px] bg-primary" aria-hidden="true" />
              <span className="text-[19px] font-semibold tracking-tight text-foreground">
                License
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-status-active/25 bg-status-active/10 px-2.5 py-1 text-[12px] font-medium text-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-status-active" aria-hidden="true" />
              {t('shell.envBadge')}
            </span>
          </div>

          <h1 className="mt-9 text-[26px] font-semibold leading-tight tracking-tight text-foreground">
            {t('login.title')}
          </h1>
          <p className="mt-2 font-serif text-[16px] italic leading-snug text-muted-foreground">
            {t('login.subtitle')}
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username">{t('login.username')}</Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                placeholder={t('login.usernamePlaceholder')}
                disabled={isSubmitting}
                autoFocus
                className="h-11"
                {...register('username')}
              />
              {errors.username && (
                <p className="text-sm text-destructive" role="alert">
                  {t(String(errors.username.message))}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t('login.password')}</Label>
                <Link
                  to="/forgot-password"
                  className="rounded-[4px] text-[13px] font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('forgotPassword.loginLink')}
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder={t('login.passwordPlaceholder')}
                  disabled={isSubmitting}
                  className="h-11 pr-11"
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={t(showPassword ? 'login.hidePassword' : 'login.showPassword')}
                  aria-pressed={showPassword}
                  className="absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-[7px] text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-destructive" role="alert">
                  {t(String(errors.password.message))}
                </p>
              )}
            </div>

            {errors.root && (
              <div
                className="flex items-start gap-2.5 rounded-[9px] border border-destructive/30 bg-destructive/10 px-3.5 py-3"
                role="alert"
              >
                <AlertCircle
                  className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
                  aria-hidden="true"
                />
                <p className="text-sm leading-snug text-foreground">{errors.root.message}</p>
              </div>
            )}

            <Button type="submit" className="h-11 w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  {t('login.signingIn')}
                </>
              ) : (
                t('login.signIn')
              )}
            </Button>
          </form>

          {/* Footer meta row */}
          <div className="mt-9 flex items-center justify-between border-t pt-4 font-mono text-xs text-faint">
            <span>{APP_VERSION}</span>
            <span>{t('login.footerMeta')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
